import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Layers, Save, Search, SlidersHorizontal, Sparkles, Users as UsersIcon } from 'lucide-react';
import { adminReports, adminTradingGroups, adminUsers } from '@/api/admin';
import type {
  ContributionTrend,
  TradingGroupSource,
  UpdateElimConfigRequest,
  VirtualUserRow,
} from '@/api/types';
import type { ProfitShareConfig, UpdateProfitShareConfigRequest } from '@/api/extras';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pill } from '@/components/widgets/Pill';
import { toast } from '@/components/ui/sonner';
import { fmtMoney, fmtPct, moneyToNumber } from '@/lib/format';
import { ApiError } from '@/lib/http';
import { downloadCsv } from '@/lib/csv';
import { cn } from '@/lib/utils';

// v2:跟单模式仅 REVERSE / FOLLOW(IGNORE 已退役,暂停改用 status)。
type ModeFilter = 'REVERSE' | 'FOLLOW' | 'ALL';
const MODE_FILTERS: { label: string; value: ModeFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: 'REVERSE', value: 'REVERSE' },
  { label: 'FOLLOW', value: 'FOLLOW' },
];

const STATUS_LABEL: Record<string, { text: string; tone: 'up' | 'warn' | 'down' | 'neutral' }> = {
  active: { text: '正常', tone: 'up' },
  suspended: { text: '已暂停', tone: 'warn' },
  dismissed: { text: '已停用', tone: 'neutral' },
  locked: { text: '已锁定', tone: 'down' },
};

function channelLabel(inviteCode?: string | null, channelAlias?: string | null): string {
  return channelAlias || inviteCode || '未归属渠道';
}

/**
 * 生效时段组的继承来源标注。
 * 解析优先级:个人指定(users.trading_group_id)→ 渠道继承(invite_codes.trading_group_id)→ 全局默认。
 * 只有「个人指定」才是本页能清除的那一档。
 */
const GROUP_SOURCE_LABEL: Record<TradingGroupSource, { text: string; tone: 'brand' | 'neutral'; title: string }> = {
  user: { text: '个人指定', tone: 'brand', title: '单用户覆盖:优先级最高,由本页设置;清除后回落到渠道继承 / 全局' },
  channel: { text: '渠道继承', tone: 'neutral', title: '来自注册渠道(邀请码)绑定的时段组;改绑请到「邀请码管理」' },
  global: { text: '全局默认', tone: 'neutral', title: '未分组:走「交易时段配置」的全局窗口' },
};

/** 用户列表 / CSV 共用的时段组显示名(未分组显示「默认(全局)」)。 */
function groupLabel(u: VirtualUserRow): string {
  return u.tradingGroupName ?? '默认(全局)';
}

function quotaForUser(u: VirtualUserRow, config?: ProfitShareConfig): { text: string; title?: string } {
  if (config && !config.fixedDailyQuotaEnabled) return { text: '未启用' };
  if (u.dailyQuota) {
    return { text: fmtMoney(u.dailyQuota), title: '后端返回的用户适用每日额度' };
  }
  if (!config) return { text: '—' };
  const matched = (config.channelQuotas ?? []).find((c) => c.inviteCode === u.inviteCode);
  if (matched) {
    return {
      text: fmtMoney(matched.effectiveDailyQuota),
      title: `渠道:${channelLabel(matched.inviteCode, matched.channelAlias)}`,
    };
  }
  return { text: fmtMoney(config.dailyQuota), title: '全部渠道统一固定日额度' };
}

function quotaCsv(u: VirtualUserRow, config?: ProfitShareConfig): string {
  const q = quotaForUser(u, config);
  return q.text === '未启用' || q.text === '未配置' ? '' : q.text;
}

export function Employees() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<ModeFilter>('ALL');
  const [search, setSearch] = useState('');
  const [cfgOpen, setCfgOpen] = useState(false);
  // 已归档(离职移出名单)用户默认不列;打开开关才拉回来(灰显 + 可撤销归档)。
  const [showArchived, setShowArchived] = useState(false);

  const listQ = useQuery({
    queryKey: ['admin', 'users', 'list', { filter, pageSize: 200, showArchived }],
    queryFn: () =>
      adminUsers.list({
        pageSize: 200,
        ...(filter !== 'ALL' ? { followMode: filter } : {}),
        ...(showArchived ? { includeArchived: true } : {}),
      }),
  });
  const quotaConfigQ = useQuery({
    queryKey: ['admin', 'profit-share', 'config'],
    queryFn: adminReports.profitShareConfig,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  const quotaConfig = quotaConfigQ.data;

  const items = useMemo(() => {
    const all = listQ.data?.items ?? [];
    if (!search) return all;
    const q = search.trim().toLowerCase();
    return all.filter(
      (u) =>
        u.userId.includes(q) ||
        (u.phone ?? '').toLowerCase().includes(q) ||
        (u.channelAlias ?? '').toLowerCase().includes(q) ||
        (u.inviteCode ?? '').toLowerCase().includes(q),
    );
  }, [listQ.data, search]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-brand" />
            注册用户管理
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            虚拟账户(余额 / 跟单模式 / 状态 / 30 天 + 今日胜率与单数)· 模式切换立即生效 · 额度重置解锁
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-ink-3" />
            <Input
              placeholder="搜索手机号 / 渠道 / 用户 ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 w-56"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => setCfgOpen(true)}>
            <SlidersHorizontal className="h-4 w-4" />
            淘汰阈值
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={items.length === 0}
            onClick={() =>
              downloadCsv('users', items, [
                { key: 'userId', header: 'userId' },
                { key: 'phone', header: '手机号' },
                { key: 'channelAlias', header: '渠道', format: (_, r) => r.channelAlias || r.inviteCode || '' },
                { key: 'tradingGroupName', header: '时段组', format: (_, r) => groupLabel(r) },
                {
                  key: 'tradingGroupSource',
                  header: '时段组来源',
                  format: (_, r) => GROUP_SOURCE_LABEL[r.tradingGroupSource ?? 'global'].text,
                },
                { key: 'followMode', header: '模式' },
                { key: 'dailyQuota', header: '每日额度', format: (_, r) => quotaCsv(r, quotaConfig) },
                { key: 'status', header: '状态' },
                { key: 'virtualBalance', header: '虚拟余额', format: (_, r) => moneyToNumber(r.virtualBalance).toFixed(2) },
                { key: 'winRate30d', header: '30d 胜率(%)', format: (v) => Number(v ?? 0).toFixed(1) },
                { key: 'totalTrades30d', header: '30d 单' },
                { key: 'winRateToday', header: '今日胜率(%)', format: (_, r) => (r.tradesToday ? Number(r.winRateToday ?? 0).toFixed(1) : '') },
                { key: 'tradesToday', header: '今日单' },
                { key: 'cumulativePnl', header: '累计 PnL(投影)', format: (_, r) => moneyToNumber(r.cumulativePnl).toFixed(2) },
                { key: 'realLegContribution', header: '账面贡献(平台)', format: (_, r) => (r.realLegContribution != null ? moneyToNumber(r.realLegContribution).toFixed(2) : '') },
                { key: 'openPositionsCount', header: '持仓笔数' },
                { key: 'suggestedMode', header: '系统建议' },
              ])
            }
          >
            <Download className="h-4 w-4" />
            导出 CSV
          </Button>
        </div>
      </header>

      <FixedDailyQuotaCard
        config={quotaConfigQ.data}
        loading={quotaConfigQ.isLoading}
        error={quotaConfigQ.error}
      />

      <div className="flex items-center gap-2 flex-wrap">
        {MODE_FILTERS.map((f) => (
          <Button
            key={f.value}
            variant={filter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.value)}
          >
            {f.label}
            {filter === f.value && listQ.data && <span className="ml-1 num text-xs opacity-80">{items.length}</span>}
          </Button>
        ))}
        <label className="ml-2 flex items-center gap-2 text-sm text-ink-2">
          <Switch checked={showArchived} onCheckedChange={setShowArchived} />
          显示已归档
          <span className="text-xs text-ink-3">(已移出名单的离职人员)</span>
        </label>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>用户列表 · {listQ.data?.total ?? '—'} 人</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {listQ.isError && (
            <div className="px-4 py-3 text-sm text-down-dim">
              无法加载用户列表:{listQ.error instanceof ApiError ? listQ.error.message : '未知错误'}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-3 text-ink-2 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left font-normal w-28">用户ID</th>
                  <th className="px-3 py-2 text-left font-normal">手机号</th>
                  <th className="px-3 py-2 text-left font-normal">渠道</th>
                  <th
                    className="px-3 py-2 text-left font-normal"
                    title="生效的交易时段组(可交易窗口)。优先级:个人指定 → 渠道继承 → 全局默认。点击单元格可设置 / 清除个人覆盖"
                  >
                    时段组
                  </th>
                  <th className="px-3 py-2 text-left font-normal w-40">跟单模式</th>
                  <th className="px-3 py-2 text-right font-normal">每日额度</th>
                  <th className="px-3 py-2 text-right font-normal">虚拟余额</th>
                  <th className="px-3 py-2 text-right font-normal">30d 胜率</th>
                  <th className="px-3 py-2 text-right font-normal">30d 单</th>
                  <th className="px-3 py-2 text-right font-normal" title="今日胜率(北京 03:00 交易日界实时);今日无成交显 —">今日胜率</th>
                  <th className="px-3 py-2 text-right font-normal" title="今日已结算笔数(北京 03:00 交易日界;胜率分母,排除保本单)">今日单</th>
                  <th className="px-3 py-2 text-right font-normal" title="用户投影盈亏 s·(1/r)·realPnL,展示用,不作淘汰判据">累计 PnL(投影)</th>
                  <th className="px-3 py-2 text-right font-normal" title="平台口径:真实对冲腿 SUM(pnl)/100+佣金+库存费,= 平台真金白银盈亏。负=平台在亏(淘汰判据)">账面贡献(平台)</th>
                  <th className="px-3 py-2 text-right font-normal">持仓</th>
                  <th className="px-3 py-2 text-left font-normal">状态</th>
                  <th className="px-3 py-2 text-left font-normal">系统建议</th>
                  <th className="px-3 py-2 text-right font-normal">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <UserRow key={u.userId} u={u} quotaConfig={quotaConfig} onChanged={invalidate} />
                ))}
                {items.length === 0 && !listQ.isLoading && (
                  <tr>
                    <td colSpan={17} className="px-3 py-8 text-center text-ink-3">
                      没有符合条件的用户
                    </td>
                  </tr>
                )}
                {listQ.isLoading && (
                  <tr>
                    <td colSpan={17} className="px-3 py-8 text-center text-ink-3">
                      加载中...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <ElimConfigDialog open={cfgOpen} onOpenChange={setCfgOpen} />
    </div>
  );
}

type ChannelQuotaDraft = {
  selected: boolean;
  amount: string;
};

function FixedDailyQuotaCard({
  config,
  loading,
  error,
}: {
  config?: ProfitShareConfig;
  loading: boolean;
  error: unknown;
}) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [defaultUsd, setDefaultUsd] = useState('1000');
  const [channelDrafts, setChannelDrafts] = useState<Record<string, ChannelQuotaDraft>>({});
  const channels = config?.channelQuotas ?? [];

  useEffect(() => {
    if (!config) return;
    setEnabled(config.fixedDailyQuotaEnabled);
    setDefaultUsd(String(config.dailyQuotaUsd));
    const next: Record<string, ChannelQuotaDraft> = {};
    for (const ch of config.channelQuotas ?? []) {
      next[ch.channelId] = {
        selected: ch.dailyQuotaUsd != null,
        amount: String(ch.dailyQuotaUsd ?? ch.effectiveDailyQuotaUsd ?? config.dailyQuotaUsd),
      };
    }
    setChannelDrafts(next);
  }, [config]);

  const save = useMutation({
    mutationFn: () => {
      const amount = Number(defaultUsd);
      if (!Number.isFinite(amount) || amount < 0.01 || amount > 1_000_000) {
        throw new Error('全部用户默认额度须在 0.01 到 1000000 美元之间');
      }
      const channelQuotas = channels.map((ch) => {
        const draft = channelDrafts[ch.channelId];
        if (!draft?.selected) {
          return { channelId: ch.channelId, dailyQuotaUsd: null };
        }
        const channelAmount = Number(draft.amount);
        if (!Number.isFinite(channelAmount) || channelAmount < 0.01 || channelAmount > 1_000_000) {
          throw new Error(`${channelLabel(ch.inviteCode, ch.channelAlias)} 的额度须在 0.01 到 1000000 美元之间`);
        }
        return { channelId: ch.channelId, dailyQuotaUsd: channelAmount };
      });
      const body: UpdateProfitShareConfigRequest = {
        fixedDailyQuotaEnabled: enabled,
        dailyQuotaUsd: amount,
        channelQuotas,
      };
      return adminReports.updateProfitShareConfig(body);
    },
    onSuccess: (res) => {
      qc.setQueryData(['admin', 'profit-share', 'config'], res.config);
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      const updated = res.applied?.updatedUsers ?? 0;
      toast.success(
        res.config.fixedDailyQuotaEnabled
          ? `固定日额度已保存,已更新 ${updated} 个用户余额`
          : '固定日额度已关闭',
      );
    },
    onError: (err) => {
      toast.error('保存失败', { description: err instanceof ApiError ? err.message : (err as Error)?.message });
    },
  });

  const setChannelSelected = (channelId: string, selected: boolean, fallbackAmount: number) => {
    setChannelDrafts((d) => ({
      ...d,
      [channelId]: {
        selected,
        amount: d[channelId]?.amount ?? String(fallbackAmount),
      },
    }));
  };
  const setChannelAmount = (channelId: string, amount: string) => {
    setChannelDrafts((d) => ({
      ...d,
      [channelId]: {
        selected: d[channelId]?.selected ?? true,
        amount,
      },
    }));
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">固定日额度模式</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {Boolean(error) && (
          <div className="text-sm text-down-dim">
            无法加载固定日额度配置:{error instanceof ApiError ? error.message : '未知错误'}
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex items-center gap-2 text-sm text-ink-1">
            <Switch checked={enabled} onCheckedChange={setEnabled} disabled={loading || save.isPending} />
            启用固定日额度
          </label>
          <Field label="全部用户默认额度(美元)">
            <Input
              type="number"
              min="0.01"
              max="1000000"
              step="0.01"
              value={defaultUsd}
              onChange={(e) => setDefaultUsd(e.target.value)}
              className="w-36"
              disabled={loading || save.isPending}
            />
          </Field>
          {config && <span className="pb-2 text-xs text-ink-3">当前默认 {fmtMoney(config.dailyQuota)}</span>}
          <Button onClick={() => save.mutate()} disabled={loading || save.isPending}>
            <Save className="h-4 w-4" />
            {save.isPending ? '保存中' : '保存并更新余额'}
          </Button>
        </div>

        <div className="border-t border-line pt-3">
          <div className="mb-2 text-xs font-semibold text-ink-2">渠道单独额度</div>
          {loading ? (
            <div className="py-3 text-sm text-ink-3">加载中...</div>
          ) : channels.length === 0 ? (
            <div className="py-3 text-sm text-ink-3">暂无渠道</div>
          ) : (
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {channels.map((ch) => {
                const draft = channelDrafts[ch.channelId] ?? {
                  selected: ch.dailyQuotaUsd != null,
                  amount: String(ch.dailyQuotaUsd ?? ch.effectiveDailyQuotaUsd ?? config?.dailyQuotaUsd ?? 1000),
                };
                return (
                  <div key={ch.channelId} className="rounded-md border border-line px-3 py-2">
                    <label className="flex items-center justify-between gap-2">
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-ink-1">{channelLabel(ch.inviteCode, ch.channelAlias)}</span>
                        <span className="block truncate text-xs text-ink-3">{ch.inviteCode}</span>
                      </span>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={draft.selected}
                        disabled={loading || save.isPending}
                        onChange={(e) =>
                          setChannelSelected(ch.channelId, e.target.checked, ch.effectiveDailyQuotaUsd)
                        }
                      />
                    </label>
                    <div className="mt-2 flex items-center gap-2">
                      <Input
                        type="number"
                        min="0.01"
                        max="1000000"
                        step="0.01"
                        value={draft.amount}
                        disabled={!draft.selected || loading || save.isPending}
                        onChange={(e) => setChannelAmount(ch.channelId, e.target.value)}
                      />
                      <span className="whitespace-nowrap text-xs text-ink-3">
                        {draft.selected ? '单独' : `继承 ${fmtMoney(config?.dailyQuota)}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** 淘汰建议阈值配置(蓝图 §9)。即时生效;系统只建议、人工判定。 */
function ElimConfigDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const cfgQ = useQuery({ queryKey: ['admin', 'elim-config'], queryFn: adminUsers.elimConfig, enabled: open });
  const [draft, setDraft] = useState<UpdateElimConfigRequest>({});
  // KPI 手数目标单独管理(空字符串=不设/取消)。
  const [kpiLots, setKpiLots] = useState<string>('');

  useEffect(() => {
    if (cfgQ.data) {
      const c = cfgQ.data;
      setDraft({
        contribLossUsd: c.contribLossUsd,
        contribNegDays: c.contribNegDays,
        winnerNegDays: c.winnerNegDays,
        winnerNegUsd: c.winnerNegUsd,
        kpiWindowDays: c.kpiWindowDays,
        idleNoOrderDays: c.idleNoOrderDays,
      });
      setKpiLots(c.kpiLotsPerWindow != null ? String(c.kpiLotsPerWindow) : '');
    }
  }, [cfgQ.data]);

  const save = useMutation({
    mutationFn: (body: UpdateElimConfigRequest) => adminUsers.updateElimConfig(body),
    onSuccess: () => {
      toast.success('淘汰建议阈值已即时生效');
      onOpenChange(false);
    },
    onError: (e) => toast.error('保存失败', { description: e instanceof ApiError ? e.message : '' }),
  });

  const onSave = () => {
    // kpiLots:空 → 传 0(取消目标);否则传数值。其余字段直接带上。
    const kpiVal = kpiLots.trim() === '' ? 0 : Number(kpiLots);
    if (!Number.isFinite(kpiVal) || kpiVal < 0) {
      toast.error('KPI 手数目标须为非负数(留空=取消)');
      return;
    }
    save.mutate({ ...draft, kpiLotsPerWindow: kpiVal });
  };

  type NumKey = keyof Omit<UpdateElimConfigRequest, 'note' | 'kpiLotsPerWindow'>;
  const num = (k: NumKey) => (typeof draft[k] === 'number' ? String(draft[k]) : '');
  const setNum = (k: NumKey, v: string) =>
    setDraft((d) => ({ ...d, [k]: v.trim() === '' ? undefined : Number(v) }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 text-brand" />
            淘汰建议阈值(§9)
          </DialogTitle>
          <DialogDescription>
            系统据此<strong>只打建议标记、人工判定,绝不自动淘汰</strong>。保存<strong className="text-warn">即时生效</strong>(写 system_config)。
          </DialogDescription>
        </DialogHeader>

        {cfgQ.isLoading ? (
          <div className="py-6 text-center text-sm text-ink-3">加载中...</div>
        ) : (
          <div className="space-y-4 text-sm">
            <section className="space-y-2">
              <div className="text-xs font-semibold text-ink-2">账面持续亏(建议停用/重置额度)</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="累计账面亏阈值(美元,负)">
                  <Input type="number" step="1" value={num('contribLossUsd')} onChange={(e) => setNum('contribLossUsd', e.target.value)} />
                </Field>
                <Field label="且连续负贡献交易日 ≥">
                  <Input type="number" min="1" max="90" value={num('contribNegDays')} onChange={(e) => setNum('contribNegDays', e.target.value)} />
                </Field>
              </div>
            </section>
            <section className="space-y-2 pt-2 border-t border-line">
              <div className="text-xs font-semibold text-ink-2">稳定赢家(建议重置额度/谨慎切 FOLLOW)</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="连续负贡献交易日 ≥">
                  <Input type="number" min="1" max="90" value={num('winnerNegDays')} onChange={(e) => setNum('winnerNegDays', e.target.value)} />
                </Field>
                <Field label="且期间累计净亏 ≥(美元)">
                  <Input type="number" min="0" value={num('winnerNegUsd')} onChange={(e) => setNum('winnerNegUsd', e.target.value)} />
                </Field>
              </div>
            </section>
            <section className="space-y-2 pt-2 border-t border-line">
              <div className="text-xs font-semibold text-ink-2">不活跃(建议停用)</div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="连续无下单天数 ≥">
                  <Input type="number" min="1" max="90" value={num('idleNoOrderDays')} onChange={(e) => setNum('idleNoOrderDays', e.target.value)} />
                </Field>
                <Field label="KPI 窗口(交易日)">
                  <Input type="number" min="1" max="90" value={num('kpiWindowDays')} onChange={(e) => setNum('kpiWindowDays', e.target.value)} />
                </Field>
                <Field label="KPI 手数目标(空=不设)">
                  <Input type="number" min="0" step="0.01" placeholder="留空=不设" value={kpiLots} onChange={(e) => setKpiLots(e.target.value)} />
                </Field>
              </div>
            </section>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>取消</Button>
          <Button onClick={onSave} disabled={save.isPending || cfgQ.isLoading}>
            {save.isPending ? '保存中...' : '保存(即时生效)'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-ink-2">{label}</Label>
      {children}
    </div>
  );
}

function UserRow({
  u,
  quotaConfig,
  onChanged,
}: {
  u: VirtualUserRow;
  quotaConfig?: ProfitShareConfig;
  onChanged: () => void;
}) {
  const pnl = moneyToNumber(u.cumulativePnl);
  // 账面贡献(平台口径):正=平台赚,负=平台在亏(淘汰判据)。字段缺省(未返回)显示「—」。
  const hasContrib = u.realLegContribution != null;
  const contrib = moneyToNumber(u.realLegContribution);
  const st = STATUS_LABEL[u.status] ?? { text: u.status, tone: 'neutral' as const };
  const groupSource = GROUP_SOURCE_LABEL[u.tradingGroupSource ?? 'global'];
  const [trendOpen, setTrendOpen] = useState(false);
  const [dispOpen, setDispOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [tempPwd, setTempPwd] = useState<string | null>(null);
  // 归档 / 撤销归档弹窗(需原因);null = 关闭。
  const [archiveMode, setArchiveMode] = useState<'archive' | 'unarchive' | null>(null);
  const quota = quotaForUser(u, quotaConfig);

  const setMode = useMutation({
    mutationFn: (m: 'REVERSE' | 'FOLLOW') => adminUsers.setFollowMode(u.userId, m),
    onSuccess: onChanged,
  });
  const suspend = useMutation({ mutationFn: () => adminUsers.suspend(u.userId), onSuccess: onChanged });
  const dismiss = useMutation({ mutationFn: () => adminUsers.dismiss(u.userId), onSuccess: onChanged });
  const activate = useMutation({
    mutationFn: () => adminUsers.activate(u.userId),
    onSuccess: () => {
      toast.success('已恢复为正常(余额与已实现盈亏保留)');
      onChanged();
    },
    onError: (e) => toast.error('恢复失败', { description: e instanceof ApiError ? e.message : '' }),
  });
  const reset = useMutation({
    mutationFn: (bal: number) => adminUsers.resetBalance(u.userId, bal),
    onSuccess: onChanged,
    onError: (e) => toast.error('重置额度失败', { description: e instanceof ApiError ? e.message : '' }),
  });
  // §6.4 提前解除当日风控冻结(联系负责人申请再次开通)。不动 status/balance。
  const riskResume = useMutation({
    mutationFn: () => adminUsers.riskResume(u.userId),
    onSuccess: (r) => {
      toast.success(
        r.cleared > 0
          ? '已解除当日冻结,亏损风控从当前时刻重新累计'
          : '已刷新亏损风控累计起点,该用户仍受当日规则限制',
      );
      onChanged();
    },
    onError: (e) => toast.error('解除当日冻结失败', { description: e instanceof ApiError ? e.message : '' }),
  });
  // 重置员工登录密码:生成临时密码(原密码立即失效),成功后弹窗展示一次供转交。
  const resetPwd = useMutation({
    mutationFn: () => adminUsers.resetPassword(u.userId),
    onSuccess: (r) => {
      setTempPwd(r.tempPassword);
      toast.success('已重置密码,请把临时密码转交本人');
    },
    onError: (e) => toast.error('重置密码失败', { description: e instanceof ApiError ? e.message : '' }),
  });

  const suggestion =
    u.suggestedMode && u.suggestedMode !== u.followMode
      ? u.suggestedMode === 'FOLLOW'
        ? { tone: 'warn' as const, text: '★ 建议 FOLLOW' }
        : { tone: 'brand' as const, text: '建议 REVERSE' }
      : null;

  // 三类质量建议标记(蓝图 §3.2)。系统只建议,人工判定;后端已按 system_config 阈值算好布尔。
  const qualityFlags = [
    u.isAccountLoss && {
      tone: 'down' as const,
      text: '账面持续亏',
      title: '累计账面贡献(平台真实腿)为负达阈值 → 建议 停用 / 重置额度',
    },
    u.isStableWinner && {
      tone: 'warn' as const,
      text: '稳定赢家',
      title: '近窗口平台真金白银净亏达阈值 → 建议 重置额度观察 / 谨慎切 FOLLOW(注意存量腿不平、转亏放大风险)',
    },
    u.isInactive && {
      tone: 'brand' as const,
      text: '不活跃',
      title:
        (u.idleDays != null ? `连续 ${u.idleDays} 天无下单` : '从未下单') +
        '(或未达手数 KPI)→ 建议 停用',
    },
  ].filter(Boolean) as { tone: 'down' | 'warn' | 'brand'; text: string; title: string }[];
  const kpi = u.kpiProgress;

  const onReset = () => {
    // 不预填默认值(防误点 0 清零余额触发爆仓);留空/非法金额拒绝,提交前二次确认。
    const input = window.prompt(
      `设置 / 重置用户 ${u.phone ?? u.userId} 的虚拟额度(美元)。\n注意:将覆盖当前余额、已实现盈亏清零。`,
      '',
    );
    if (input == null) return;
    const bal = Number(input.trim());
    if (input.trim() === '' || !Number.isFinite(bal) || bal < 0) {
      toast.error('请输入有效的非负金额(美元)');
      return;
    }
    if (
      !window.confirm(
        `确认把 ${u.phone ?? u.userId} 的虚拟额度重置为 $${bal.toFixed(2)}?\n当前余额将被覆盖、已实现盈亏清零(金额为 0 会使净值归零并锁定账户)。`,
      )
    )
      return;
    reset.mutate(bal);
  };

  const onResetPwd = () => {
    if (
      !window.confirm(
        `确认为 ${u.phone ?? u.userId} 生成新的临时密码?\n原密码立即失效,需把临时密码转交本人,并让其首次登录后尽快修改。`,
      )
    )
      return;
    resetPwd.mutate();
  };

  return (
    <tr className="border-t border-line row-hover">
      <td className="px-3 py-2 text-ink-1 num">{u.userId}</td>
      <td className="px-3 py-2 num text-ink-1">{u.phone || '—'}</td>
      <td className="px-3 py-2 text-ink-2" title={u.inviteCode ? `邀请码 ${u.inviteCode}` : undefined}>
        {u.channelAlias || u.inviteCode || '—'}
      </td>
      {/* 生效时段组 = 按优先级解析出的结果;来源标签点破它是个人指定还是渠道继承(决定「清除覆盖」能不能点)。 */}
      <td className="px-3 py-2">
        <button
          type="button"
          className="text-left hover:underline"
          title="点击设置 / 清除该用户的时段组个人覆盖(优先级高于渠道)"
          onClick={() => setGroupOpen(true)}
        >
          <span className="block text-ink-1">{groupLabel(u)}</span>
          <span className="block mt-0.5" title={groupSource.title}>
            <Pill tone={groupSource.tone}>{groupSource.text}</Pill>
          </span>
        </button>
      </td>
      <td className="px-3 py-2">
        <select
          className="bg-bg-2 border border-line rounded px-2 py-1 text-xs"
          value={u.followMode}
          disabled={setMode.isPending || u.status === 'dismissed'}
          onChange={(e) => setMode.mutate(e.target.value as 'REVERSE' | 'FOLLOW')}
        >
          <option value="REVERSE">REVERSE</option>
          <option value="FOLLOW">FOLLOW</option>
        </select>
      </td>
      <td className="px-3 py-2 text-right num text-ink-1" title={quota.title}>
        {quota.text}
      </td>
      <td className="px-3 py-2 text-right num">{fmtMoney(u.virtualBalance)}</td>
      <td className="px-3 py-2 text-right num text-ink-1">{fmtPct(u.winRate30d)}</td>
      <td className="px-3 py-2 text-right num text-ink-1">{u.totalTrades30d ?? '—'}</td>
      <td className="px-3 py-2 text-right num text-ink-1">{u.tradesToday ? fmtPct(u.winRateToday) : '—'}</td>
      <td className="px-3 py-2 text-right num text-ink-1">{u.tradesToday ?? '—'}</td>
      <td className={cn('px-3 py-2 text-right num', pnl >= 0 ? 'text-up' : 'text-down')}>
        {fmtMoney(u.cumulativePnl, { sign: true })}
      </td>
      <td className={cn('px-3 py-2 text-right num font-medium', contrib >= 0 ? 'text-up' : 'text-down')}>
        {hasContrib ? (
          <button
            type="button"
            className="hover:underline"
            title="平台账面贡献:正=平台赚,负=平台真金白银在亏(淘汰判据)· 点击看按交易日趋势"
            onClick={() => setTrendOpen(true)}
          >
            {fmtMoney(u.realLegContribution, { sign: true })}
          </button>
        ) : (
          '—'
        )}
      </td>
      <td className="px-3 py-2 text-right num text-ink-1">{u.openPositionsCount ?? 0}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-1">
          <Pill tone={st.tone}>{st.text}</Pill>
          {u.archived && (
            <span
              title={`已移出名单${u.archiveReason ? ` · 原因:${u.archiveReason}` : ''}。历史分润、考核、成交记录保留可查,可撤销归档;但此人及其金额已不计入任何分润统计 ——「月度分润」报表与渠道小计/总合计、「平台经营」页的平台当月分润、组长在 App 端看到的本组分润,均不再包含。报表页打开「显示已停用渠道 / 已归档人员」开关可把此人连同金额拉回来核对。`}
            >
              <Pill tone="neutral">已归档</Pill>
            </span>
          )}
          {u.riskPaused && (
            <span title="当日亏损达上限被冻结(当日禁交易,次日 03:00 自动恢复)">
              <Pill tone="down">当日冻结</Pill>
            </span>
          )}
          {!u.riskPaused && u.riskLossResetActive && (
            <span title="亏损风控已从最近一次解除/恢复/重置时刻重新累计;全天盈亏统计不受影响">
              <Pill tone="warn">已重置</Pill>
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-col items-start gap-1">
          {suggestion ? (
            <Pill tone={suggestion.tone}>
              <Sparkles className="h-3 w-3" />
              {suggestion.text}
            </Pill>
          ) : qualityFlags.length === 0 ? (
            <span className="text-ink-3 text-xs">保持当前</span>
          ) : null}
          {qualityFlags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {qualityFlags.map((f) => (
                <span key={f.text} title={f.title}>
                  <Pill tone={f.tone}>{f.text}</Pill>
                </span>
              ))}
              <button
                type="button"
                className="text-[11px] text-brand hover:underline ml-0.5"
                onClick={() => setDispOpen(true)}
                title="按建议标记一键处置"
              >
                一键处置
              </button>
            </div>
          )}
          {kpi && (
            <span
              className={cn('text-[11px] num', kpi.met ? 'text-up' : 'text-ink-3')}
              title={`KPI 手数:${kpi.completedLots.toFixed(2)} / ${kpi.targetLots.toFixed(2)} 手(近 ${kpi.windowDays} 交易日)`}
            >
              KPI {kpi.progressPct.toFixed(0)}%
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-1.5">
          {u.riskPaused && (
            <Button
              variant="outline"
              size="sm"
              disabled={riskResume.isPending}
              onClick={() => riskResume.mutate()}
              title="解除当日亏损冻结:不动余额/状态,亏损风控从解除时刻重新累计,该用户仍受当日规则限制"
            >
              解除当日冻结
            </Button>
          )}
          {/* 已归档者不给「恢复」入口:归档→在岗必须两步(先撤销归档回到「已停用」,再恢复),
              一步恢复会造出「已移出名单却仍在发额度/记考核」的矛盾态。后端 activate 也会返 409。 */}
          {(u.status === 'suspended' || u.status === 'dismissed') && !u.archived && (
            <Button variant="ghost" size="sm" disabled={activate.isPending} onClick={() => activate.mutate()} title="恢复为正常(取消暂停/停用,保留余额)">
              恢复
            </Button>
          )}
          {u.status === 'active' && (
            <Button variant="ghost" size="sm" disabled={suspend.isPending} onClick={() => suspend.mutate()}>
              暂停
            </Button>
          )}
          {u.status !== 'dismissed' && (
            <Button variant="ghost" size="sm" disabled={dismiss.isPending} onClick={() => dismiss.mutate()}>
              停用
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            disabled={reset.isPending || u.status === 'dismissed'}
            title={u.status === 'dismissed' ? '已停用用户请先「恢复」再重置额度' : undefined}
            onClick={onReset}
          >
            重置额度
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={resetPwd.isPending}
            title="生成临时密码(原密码立即失效),转交本人,首登后尽快改密"
            onClick={onResetPwd}
          >
            重置密码
          </Button>
          {/* 离职人员从名单里清掉:软删(先平仓禁登录再打标),历史分润/考核/成交全保留可查。 */}
          {u.archived ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-warn"
              title="撤销归档:放回名单,状态仍为「已停用」,要恢复交易须再点「恢复」"
              onClick={() => setArchiveMode('unarchive')}
            >
              撤销归档
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-down"
              title="移出名单:先结算其全部持仓并禁止登录,再从列表隐藏;历史数据保留,可撤销"
              onClick={() => setArchiveMode('archive')}
            >
              移出名单
            </Button>
          )}
        </div>
        <ArchiveDialog
          mode={archiveMode}
          u={u}
          onClose={() => setArchiveMode(null)}
          onDone={onChanged}
        />
        <ContributionTrendDialog
          open={trendOpen}
          onOpenChange={setTrendOpen}
          userId={u.userId}
          phone={u.phone}
        />
        <UserTradingGroupDialog open={groupOpen} onOpenChange={setGroupOpen} u={u} onDone={onChanged} />
        <DisposalDialog
          open={dispOpen}
          onOpenChange={setDispOpen}
          u={u}
          flags={qualityFlags}
          busy={suspend.isPending || dismiss.isPending || reset.isPending || setMode.isPending}
          onSuspend={() => suspend.mutate()}
          onDismiss={() => dismiss.mutate()}
          onReset={onReset}
          onToggleMode={() => setMode.mutate(u.followMode === 'REVERSE' ? 'FOLLOW' : 'REVERSE')}
        />
        <Dialog open={tempPwd !== null} onOpenChange={(v) => !v && setTempPwd(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>临时密码 · {u.phone || u.userId}</DialogTitle>
              <DialogDescription>
                以下临时密码<strong>仅显示这一次</strong>。请通过安全渠道转交本人,并提醒其<strong>首次登录后尽快修改密码</strong>。
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2 rounded-md border border-line bg-bg-2 px-3 py-2">
              <code className="num text-base font-mono text-ink-0 break-all flex-1">{tempPwd}</code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (tempPwd) {
                    void navigator.clipboard?.writeText(tempPwd);
                    toast.success('临时密码已复制');
                  }
                }}
              >
                复制
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={() => setTempPwd(null)}>我已转交</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </td>
    </tr>
  );
}

/**
 * B3 建议标记「一键处置」(蓝图 §3.2/§3.3)。按三类标记给出**推荐处置**(★),点击即调已有端点。
 * 系统只建议、人工判定:此处仅把"标记→推荐动作"直连,执行仍是管理员点击,无任何自动处置。
 */
function DisposalDialog({
  open,
  onOpenChange,
  u,
  flags,
  busy,
  onSuspend,
  onDismiss,
  onReset,
  onToggleMode,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  u: VirtualUserRow;
  flags: { text: string; title: string }[];
  busy: boolean;
  onSuspend: () => void;
  onDismiss: () => void;
  onReset: () => void;
  onToggleMode: () => void;
}) {
  // 标记 → 推荐处置(蓝图 §3.2):账面亏→停用/重置;不活跃→停用;稳定赢家→重置/谨慎切 FOLLOW。
  const recReset = !!(u.isStableWinner || u.isAccountLoss);
  const recDismiss = !!(u.isAccountLoss || u.isInactive);
  const recMode = !!u.isStableWinner && u.followMode === 'REVERSE';
  const run = (fn: () => void) => {
    fn();
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>处置建议 · {u.phone || u.userId}</DialogTitle>
          <DialogDescription>
            系统据建议标记给出推荐处置(★),<strong>是否处置由你判定</strong>。处置即时生效(暂停/停用会平掉其真实仓)。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <ul className="space-y-1 text-xs text-ink-2">
            {flags.map((f) => (
              <li key={f.text}>· {f.title}</li>
            ))}
          </ul>
          {u.isStableWinner && (
            <div className="rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn-dim">
              切 FOLLOW 只改未来新单方向,<strong>不平/不反转存量亏损腿</strong>;且"跟他赚"是历史外推,他转亏则平台同向放大亏损。首选「重置额度 + 等自然平仓」,切 FOLLOW 需自担反转风险。
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <DispBtn label="暂停" rec={false} disabled={busy || u.status !== 'active'} onClick={() => run(onSuspend)} />
            <DispBtn label="停用" rec={recDismiss} disabled={busy || u.status === 'dismissed'} onClick={() => run(onDismiss)} />
            <DispBtn label="重置额度" rec={recReset} disabled={busy || u.status === 'dismissed'} onClick={() => run(onReset)} />
            <DispBtn
              label={u.followMode === 'REVERSE' ? '切 FOLLOW' : '切 REVERSE'}
              rec={recMode}
              disabled={busy || u.status === 'dismissed'}
              onClick={() => run(onToggleMode)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>关闭</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DispBtn({ label, rec, disabled, onClick }: { label: string; rec: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <Button variant={rec ? 'default' : 'outline'} size="sm" disabled={disabled} onClick={onClick}>
      {rec && <Sparkles className="h-3.5 w-3.5" />}
      {rec ? `★ ${label}` : label}
    </Button>
  );
}

/** 账面贡献(平台口径)按交易日趋势(蓝图 §3.2)。日贡献柱状(涨绿跌红)+ 累计折线。 */
/**
 * 归档 / 撤销归档确认弹窗。**软删**:不物理删除任何数据 —— users 被 deals / virtual_legs /
 * 考核记录等 10+ 处外键引用,物理删除会毁掉历史分润的可复算性与审计链。
 * 与强平/改判同规格:原因(≥4 字)。
 */
function ArchiveDialog({
  mode,
  u,
  onClose,
  onDone,
}: {
  mode: 'archive' | 'unarchive' | null;
  u: VirtualUserRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (mode) {
      setReason('');
    }
  }, [mode]);

  const run = useMutation({
    mutationFn: () => {
      const body = { reason: reason.trim() };
      return mode === 'unarchive' ? adminUsers.unarchive(u.userId, body) : adminUsers.archive(u.userId, body);
    },
    onSuccess: () => {
      toast.success(mode === 'unarchive' ? '已撤销归档' : '已移出名单', {
        description:
          mode === 'unarchive'
            ? '该用户回到「已停用」,要恢复交易请再点「恢复」;各处分润统计也会重新默认计入该用户'
            : '持仓已结算、禁止登录;历史记录保留可查,但此人与其金额不再计入任何分润统计(报表页开「显示已停用渠道 / 已归档人员」可拉回核对)',
      });
      onDone();
      onClose();
    },
    onError: (e) => toast.error('操作失败', { description: e instanceof ApiError ? e.message : '' }),
  });

  const reasonOk = reason.trim().length >= 4;

  return (
    <Dialog open={!!mode} onOpenChange={(v) => !v && !run.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'unarchive' ? '撤销归档' : '移出名单(归档)'}</DialogTitle>
          <DialogDescription>
            {mode === 'unarchive' ? (
              <>
                把 <strong>{u.phone || u.userId}</strong> 放回名单,状态仍为「已停用」。
                要让其重新登录交易,须再点「恢复」—— 两步设计防误操作。
              </>
            ) : (
              <>
                将 <strong>{u.phone || u.userId}</strong> 移出后台名单。执行顺序:
                <strong>先结算其全部持仓 + 禁止登录 + 清除其负责人指派</strong>,再从列表隐藏。
                <br />
                <strong>不会删除任何历史数据</strong>:该用户的历史分润、考核、成交记录全部保留可查,
                归档可随时撤销。
                <br />
                但归档后,<strong>此人及其金额将从所有分润统计中一并消失</strong>:「月度分润」报表
                (含渠道小计、总合计与 Excel 导出)、「平台经营」页的「平台当月分润」,
                以及组长在 App 端看到的本组分润。在报表页打开「显示已停用渠道 / 已归档人员」开关,
                可把此人连同金额拉回来核对与发放。
                <br />
                <strong>建议先完成当月分润结算再归档</strong>,否则当月已产生的分润在默认视图里看不到,容易漏发。
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="arc-reason">操作原因(至少 4 字)</Label>
            <Input
              id="arc-reason"
              placeholder={mode === 'unarchive' ? '例如:误操作归档,需恢复' : '例如:已离职,2026-07 结算后移出'}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={run.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={run.isPending}>
            取消
          </Button>
          <Button
            variant={mode === 'unarchive' ? 'default' : 'destructive'}
            disabled={!reasonOk || run.isPending}
            onClick={() => run.mutate()}
          >
            {run.isPending ? '处理中…' : mode === 'unarchive' ? '确认撤销' : '确认移出'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * 单用户「时段组」覆盖(users.trading_group_id)。
 * 用于演示 / 特殊账户:不必为它单独建渠道码,也不污染渠道转化数与分润归属。
 *
 * 三态:不覆盖 = 传 null(回落渠道继承 / 全局)、选某组 = 传该组 id、不打开弹窗 = 字段缺省不动。
 * 注意列表里的 tradingGroupId 是**解析结果**,只有 tradingGroupSource==='user' 时它才等于本列的值。
 */
function UserTradingGroupDialog({
  open,
  onOpenChange,
  u,
  onDone,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  u: VirtualUserRow;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const currentOverride = u.tradingGroupSource === 'user' ? (u.tradingGroupId ?? null) : null;
  const [selected, setSelected] = useState<string | null>(currentOverride);

  const groupsQ = useQuery({
    queryKey: ['admin', 'trading-groups'],
    queryFn: adminTradingGroups.list,
    enabled: open,
  });
  const groups = groupsQ.data?.items ?? [];

  useEffect(() => {
    if (open) setSelected(currentOverride);
  }, [open, currentOverride]);

  const save = useMutation({
    mutationFn: (tradingGroupId: string | null) => adminUsers.update(u.userId, { tradingGroupId }),
    onSuccess: (_, tradingGroupId) => {
      toast.success(tradingGroupId ? '已设置个人时段组' : '已清除个人覆盖', {
        description: tradingGroupId
          ? '立即生效;已开仓的持仓归属不回溯(当晚分组日报仍按旧组算)'
          : '该用户回落到渠道继承 / 全局时段',
      });
      void qc.invalidateQueries({ queryKey: ['admin', 'trading-groups'] });
      onDone();
      onOpenChange(false);
    },
    onError: (e) => toast.error('保存失败', { description: e instanceof ApiError ? e.message : '' }),
  });

  const unchanged = currentOverride === selected;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !save.isPending && onOpenChange(false)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-brand" />
            时段组 · {u.phone || u.userId}
          </DialogTitle>
          <DialogDescription>
            单用户覆盖(优先级最高:<strong>个人 → 渠道 → 全局</strong>)。当前生效:
            <strong>{groupLabel(u)}</strong>({GROUP_SOURCE_LABEL[u.tradingGroupSource ?? 'global'].text})。
            改绑<strong>立即生效</strong>,该用户的可交易窗口与收盘时刻随新组走,
            <strong className="text-warn">请避开其持仓期操作</strong>;已开仓的虚拟腿组归属不回溯。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label className="flex items-center gap-2 rounded-md border border-line px-3 py-2 text-sm cursor-pointer row-hover">
            <input
              type="radio"
              name={`user-group-${u.userId}`}
              className="h-4 w-4"
              checked={selected === null}
              disabled={save.isPending}
              onChange={() => setSelected(null)}
            />
            <span className="text-ink-2">
              不覆盖(跟随渠道 / 全局){currentOverride ? '(清除当前个人指定)' : ''}
            </span>
          </label>

          <div className="max-h-72 overflow-y-auto rounded-md border border-line divide-y divide-line/60">
            {groupsQ.isLoading && <div className="px-3 py-8 text-center text-sm text-ink-3">加载中...</div>}
            {groupsQ.isError && (
              <div className="px-3 py-8 text-center text-sm text-down-dim">
                无法加载时段组:{groupsQ.error instanceof ApiError ? groupsQ.error.message : '未知错误'}
              </div>
            )}
            {!groupsQ.isLoading && !groupsQ.isError && groups.length === 0 && (
              <div className="px-3 py-8 text-center text-sm text-ink-3">
                还没有时段组,请先到「交易时段组」页新建
              </div>
            )}
            {groups.map((g) => (
              <label key={g.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer row-hover">
                <input
                  type="radio"
                  name={`user-group-${u.userId}`}
                  className="h-4 w-4"
                  checked={selected === g.id}
                  disabled={save.isPending}
                  onChange={() => setSelected(g.id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ink-1">
                    {g.name}
                    {!g.enabled && <span className="ml-1.5 text-xs text-warn">(已停用,组内用户回退全局)</span>}
                  </span>
                  <span className="block truncate text-xs text-ink-3 num">
                    {g.alwaysOn
                      ? '24 小时(持续开放)'
                      : `${String(g.openHour).padStart(2, '0')}:${String(g.openMinute ?? 0).padStart(2, '0')}-${String(g.forceCloseHour).padStart(2, '0')}:${String(g.forceCloseMinute ?? 0).padStart(2, '0')} · ${g.tradingDays.join(' ')}`}
                    {' · '}
                    {g.userCount} 人
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            取消
          </Button>
          <Button onClick={() => save.mutate(selected)} disabled={save.isPending || unchanged}>
            {save.isPending ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContributionTrendDialog({
  open,
  onOpenChange,
  userId,
  phone,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  userId: string;
  phone?: string;
}) {
  const q = useQuery({
    queryKey: ['admin', 'contribution-trend', userId],
    queryFn: () => adminUsers.contributionTrend(userId, 30),
    enabled: open,
  });
  const points = q.data?.points ?? [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>账面贡献趋势 · {phone || userId}</DialogTitle>
          <DialogDescription>
            平台口径(真实对冲腿)按<strong>交易日(北京 03:00 界)</strong>的日贡献(柱)与累计贡献(线)。负=平台真金白银在亏。
          </DialogDescription>
        </DialogHeader>
        {q.isLoading ? (
          <div className="py-10 text-center text-sm text-ink-3">加载中...</div>
        ) : q.isError ? (
          <div className="py-10 text-center text-sm text-down-dim">
            加载失败:{q.error instanceof ApiError ? q.error.message : '未知错误'}
          </div>
        ) : points.length === 0 ? (
          <div className="py-10 text-center text-sm text-ink-3">近 30 交易日无成交</div>
        ) : (
          <TrendChart points={points} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function TrendChart({ points }: { points: ContributionTrend['points'] }) {
  const W = 600;
  const H = 220;
  const pad = { l: 8, r: 8, t: 12, b: 28 };
  const day = points.map((p) => moneyToNumber(p.dayContribution));
  const cum = points.map((p) => moneyToNumber(p.cumContribution));
  const all = [...day, ...cum, 0];
  const min = Math.min(...all);
  const max = Math.max(...all);
  const span = max - min || 1;
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const x = (i: number) => pad.l + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = (v: number) => pad.t + innerH - ((v - min) / span) * innerH;
  const zeroY = y(0);
  const barW = Math.max(2, Math.min(18, innerW / points.length - 3));
  const cumPath = cum.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 320 }}>
        {/* 零轴 */}
        <line x1={pad.l} y1={zeroY} x2={W - pad.r} y2={zeroY} stroke="rgb(var(--c-line))" strokeWidth="1" />
        {/* 日贡献柱 */}
        {day.map((v, i) => {
          const yy = y(v);
          const h = Math.abs(yy - zeroY);
          const up = v >= 0;
          return (
            <rect
              key={i}
              x={x(i) - barW / 2}
              y={up ? yy : zeroY}
              width={barW}
              height={Math.max(1, h)}
              fill={up ? 'rgb(var(--c-up))' : 'rgb(var(--c-down))'}
              opacity={0.55}
            >
              <title>{`${points[i].date}: 日 ${v >= 0 ? '+' : ''}${v.toFixed(2)} / 累计 ${cum[i].toFixed(2)}`}</title>
            </rect>
          );
        })}
        {/* 累计折线 */}
        <path d={cumPath} fill="none" stroke="rgb(var(--c-brand))" strokeWidth="1.5" />
        {/* 首末日期 */}
        <text x={pad.l} y={H - 8} fontSize="10" fill="rgb(var(--c-ink-3))">{points[0].date.slice(5)}</text>
        <text x={W - pad.r} y={H - 8} fontSize="10" textAnchor="end" fill="rgb(var(--c-ink-3))">
          {points[points.length - 1].date.slice(5)}
        </text>
      </svg>
      <div className="mt-2 flex items-center justify-center gap-4 text-xs text-ink-3">
        <span><span className="inline-block w-3 h-2 align-middle" style={{ background: 'rgb(var(--c-up))', opacity: 0.55 }} /> / <span className="inline-block w-3 h-2 align-middle" style={{ background: 'rgb(var(--c-down))', opacity: 0.55 }} /> 日贡献</span>
        <span><span className="inline-block w-4 h-0.5 align-middle" style={{ background: 'rgb(var(--c-brand))' }} /> 累计</span>
      </div>
    </div>
  );
}
